import { useEffect, type ReactNode } from 'react';
import {
  Box,
  Flex,
  HStack,
  Heading,
  IconButton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { fullScreenPageAtom } from '../../store';
import { useT } from '../../i18n';

// A full-screen page with its own back button and a big title (Living Room,
// like Starred and Calls). Used by the account and family pages.
export function PageScaffold({
  title,
  subtitle,
  actions,
  backTo,
  children,
  maxW = '720px',
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Buttons on the right of the top bar. */
  actions?: ReactNode;
  /** Where Back goes; history back when omitted. */
  backTo?: string;
  children: ReactNode;
  maxW?: string;
}) {
  const navigate = useNavigate();
  const setFullScreen = useSetAtom(fullScreenPageAtom);
  const t = useT();
  useEffect(() => {
    setFullScreen(true);
    return () => setFullScreen(false);
  }, [setFullScreen]);

  return (
    <Flex direction="column" h="full" maxW={maxW} mx="auto" w="full">
      <HStack
        spacing={1}
        px={2}
        pt={{ base: 'max(env(safe-area-inset-top), 0.5rem)', md: 2 }}
        minH="56px"
        flexShrink={0}
      >
        <IconButton
          aria-label={t('common.back')}
          icon={<ArrowLeft size={24} />}
          variant="ghost"
          colorScheme="gray"
          onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
        />
        <Box flex="1" />
        {actions}
      </HStack>
      <Box
        flex="1"
        minH={0}
        overflowY="auto"
        sx={{ overscrollBehaviorY: 'contain' }}
      >
        <Stack spacing={1} px={4} pb={4}>
          {typeof title === 'string' ? (
            <Heading
              as="h1"
              fontSize="32px"
              lineHeight={1.05}
              letterSpacing="-0.02em"
            >
              {title}
            </Heading>
          ) : (
            title
          )}
          {subtitle && (
            <Text fontSize="15px" color="muted">
              {subtitle}
            </Text>
          )}
        </Stack>
        <Box px={4} pb="max(env(safe-area-inset-bottom), 32px)">
          {children}
        </Box>
      </Box>
    </Flex>
  );
}

// A titled card of settings or details (Living Room).
export function Section({
  title,
  description,
  action,
  children,
}: {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box
      as="section"
      bg="surface"
      borderWidth="1px"
      borderColor="line"
      borderRadius="card"
      p={4}
      mb={3}
    >
      {(title || action) && (
        <HStack align="flex-start" spacing={3} mb={description ? 1 : 3}>
          {title && (
            <Heading as="h2" fontSize="20px" flex="1">
              {title}
            </Heading>
          )}
          {action}
        </HStack>
      )}
      {description && (
        <Text fontSize="15px" color="muted" mb={3}>
          {description}
        </Text>
      )}
      {children}
    </Box>
  );
}

/** A label on the left, a value or control on the right, 56px tall. */
export function SettingRow({
  label,
  hint,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <HStack
      minH="56px"
      spacing={3}
      py={2}
      borderTopWidth="1px"
      borderColor="line"
      _first={{ borderTopWidth: 0 }}
    >
      <Box flex="1" minW={0}>
        <Text fontSize="17px" fontWeight={600}>
          {label}
        </Text>
        {hint && (
          <Text fontSize="14px" color="muted">
            {hint}
          </Text>
        )}
      </Box>
      {children}
    </HStack>
  );
}
